// AWS Amplify v6 API configuration
import { generateClient } from 'aws-amplify/api';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { AWS_CONFIGURATION } from '../config';

// Configure Amplify with the updated configuration
Amplify.configure(AWS_CONFIGURATION);

// Debug function to check the authentication state and token validity
async function checkAuthState() {
  try {
    console.log("Authentication debug: Using Cognito User Pools authentication mode");
    console.log("Authentication debug: API endpoint:", AWS_CONFIGURATION.API.GraphQL.endpoint);
    
    try {
      const session = await fetchAuthSession();
      
      if (!session?.tokens?.idToken) {
        console.log("No ID token found - user may not be authenticated");
        return false;
      } else {
        const expirationTime = session.tokens.idToken.payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeToExpiration = expirationTime - currentTime;
        
        console.log(`User authenticated as: ${session.tokens.idToken.payload.email || session.tokens.idToken.payload['cognito:username']}`);
        console.log(`Token expires in: ${Math.floor(timeToExpiration / 60000)} minutes`);
      }
    } catch (authError) {
      console.error("Auth session error:", authError);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error in auth check:", error);
    return false;
  }
}

// Run auth check on startup
checkAuthState().then(isConfigured => {
  if (isConfigured) {
    console.log("API configuration appears valid");
  } else {
    console.warn("API configuration may have issues - user may need to sign in");
  }
});

// Create a GraphQL client with Cognito user pools authentication
export const client = generateClient({
  authMode: 'userPool',
  defaultQueryOptions: {
    fetchPolicy: 'network-only',
    errorPolicy: 'all'
  }
});

// Helper function for GraphQL operations
export const graphqlOperation = (query, variables = {}) => {
  // Log operations to help with debugging
  if (query.includes('subscription ')) {
    console.log(`GraphQL Subscription: ${query.split('{')[0].trim()}`);
  } else {
    console.log(`GraphQL ${query.includes('mutation') ? 'Mutation' : 'Query'}: ${query.split('{')[0].trim()}`);
  }
  
  return {
    query,
    variables
  };
};

// For debugging GraphQL errors
function printGraphQLError(error) {
  console.group('GraphQL Error Details:');
  console.error('Error message:', error.message);
  
  if (error.networkError) {
    console.error('Network error:', error.networkError);
    if (error.networkError.statusCode) {
      console.error('Status code:', error.networkError.statusCode);
    }
  }
  
  if (error.graphQLErrors && error.graphQLErrors.length) {
    console.error('GraphQL errors:');
    error.graphQLErrors.forEach((err, i) => {
      console.group(`Error ${i + 1}:`);
      console.error('Message:', err.message);
      if (err.path) console.error('Path:', err.path);
      if (err.extensions) console.error('Extensions:', err.extensions);
      if (err.locations) console.error('Locations:', err.locations);
      console.groupEnd();
    });
  }
  console.groupEnd();
}

// Export the graphql method with enhanced error handling
export const graphql = async (operation) => {
  try {
    const result = await client.graphql(operation);
    
    // Check for GraphQL errors
    if (result.errors && result.errors.length > 0) {
      console.error('GraphQL request completed with errors:');
      result.errors.forEach((err, index) => {
        console.error(`Error ${index + 1}:`, err);
        if (err.message) console.error('Message:', err.message);
        if (err.path) console.error('Path:', err.path);
        if (err.locations) console.error('Locations:', err.locations);
        if (err.extensions) console.error('Extensions:', err.extensions);
      });
    }
    
    return result;
  } catch (error) {
    console.error('GraphQL operation failed:');
    printGraphQLError(error);
    throw error;
  }
};